import type { SidebarEntry } from "@/api/sidebarEntryAPI";
import { createDashboardProfile } from "@/dashboard/api/dashboardProfileAPI";
import { dashboardProfileOptions, dashboardProfilesQueryKey } from "@/dashboard/api/queryApi";
import { queryClient as dashboardQueryClient } from "@/dashboard/queryClient";
import { DashboardProfileNameDialog } from "@/dashboard/components/DashboardWidgets/DashboardProfileNameDialog";
import { useDashboardCustomization } from "@/dashboard/hooks/useDashboardCustomization";
import { createDefaultDashboardProfileConfig } from "@/dashboard/utils/dashboardProfileConfig";
import type { DashboardProfileMetadata } from "@/dashboard/utils/dashboard.types";
import { useSidebarRefresh } from "@/features/hub/sidebarRefreshContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faChevronDown, faPlus } from "@fortawesome/sharp-regular-svg-icons";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
    Button,
    cn,
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "frontend-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type DashboardEntry = Extract<SidebarEntry, { type: "DASHBOARD" }> & {
    navigation: Extract<NonNullable<SidebarEntry["navigation"]>, { type: "DASHBOARD" }>;
};

const profileLinkClassName =
    "text-foreground flex px-5 py-2 pl-11 text-[13px] font-medium leading-[1.25] transition-colors hover:bg-muted data-[status=active]:bg-muted";

export default function SidebarDashboardEntry({
    entry,
    customerId,
}: {
    entry: DashboardEntry;
    customerId: number;
}) {
    const { t } = useTranslation("hse");
    const { t: translateCommon } = useTranslation();
    const navigate = useNavigate();
    const pathname = useRouterState({ select: (state) => state.location.pathname });
    const sidebarRefresh = useSidebarRefresh();
    const { requestDiscardConfirmation } = useDashboardCustomization();
    const [isCreateProfileDialogOpen, setIsCreateProfileDialogOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const createProfileMutation = useMutation({
        mutationFn: (name: string) =>
            createDashboardProfile(entry.navigation.productInstanceId, {
                name,
                config: createDefaultDashboardProfileConfig(),
            }),
    });

    const handleCreateProfile = async (name: string) => {
        const profile = await createProfileMutation.mutateAsync(name);
        const profilesQueryKey = dashboardProfilesQueryKey(entry.navigation.productInstanceId);

        dashboardQueryClient.setQueryData(
            dashboardProfileOptions(entry.navigation.productInstanceId, profile.id).queryKey,
            profile,
        );
        dashboardQueryClient.setQueryData<DashboardProfileMetadata[]>(
            profilesQueryKey,
            (profiles) => {
                const createdProfile: DashboardProfileMetadata = {
                    id: profile.id,
                    name: profile.name,
                    createdAt: profile.createdAt,
                    updatedAt: profile.updatedAt,
                };

                return profiles
                    ? [
                          ...profiles.filter(
                              (existingProfile) => existingProfile.id !== profile.id,
                          ),
                          createdProfile,
                      ]
                    : [createdProfile];
            },
        );
        void dashboardQueryClient.invalidateQueries({ queryKey: profilesQueryKey });
        await sidebarRefresh?.refreshSidebar();
        await navigate({
            to: "/$customerId/dashboard/$profileId",
            params: {
                customerId: String(customerId),
                profileId: String(profile.id),
            },
        });
        return profile;
    };

    const dashboardPath = `/${customerId}${entry.navigation.mountPath}`;
    const isDashboardRouteActive =
        pathname === dashboardPath || pathname.startsWith(`${dashboardPath}/`);

    if (entry.profiles.length === 0) {
        return (
            <Link
                to="/$customerId/dashboard"
                params={{ customerId: String(customerId) }}
                activeOptions={{ exact: false }}
                className="text-foreground flex w-full items-center gap-2.5 px-5 py-2.5 text-left transition-colors hover:bg-muted data-[status=active]:bg-muted"
            >
                <span className="text-muted-foreground inline-flex h-4 w-4 shrink-0 items-center justify-center">
                    <FontAwesomeIcon icon={faChartLine} />
                </span>
                <span className="block min-w-0 flex-1 truncate text-[14px] font-medium leading-[1.25]">
                    {entry.name}
                </span>
            </Link>
        );
    }

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger
                className={cn(
                    "text-foreground flex w-full cursor-pointer items-center gap-2.5 px-5 py-2.5 text-left transition-colors hover:bg-muted data-[panel-open]:[&_.sidebar-chevron]:rotate-180",
                    !isOpen && isDashboardRouteActive && "bg-muted",
                )}
                aria-label={translateCommon("hub.toggleDashboardInstance", { name: entry.name })}
            >
                <span className="text-muted-foreground inline-flex h-4 w-4 shrink-0 items-center justify-center">
                    <FontAwesomeIcon icon={faChartLine} />
                </span>
                <span className="block min-w-0 flex-1 truncate text-[14px] font-medium leading-[1.25]">
                    {entry.name}
                </span>
                <span className="text-muted-foreground inline-flex h-4 w-4 shrink-0 items-center justify-center">
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className="sidebar-chevron transition-transform"
                    />
                </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="flex flex-col gap-0.5 pb-1">
                    {entry.profiles.map((profile) => (
                        <Link
                            key={profile.id}
                            to="/$customerId/dashboard/$profileId"
                            params={{
                                customerId: String(customerId),
                                profileId: String(profile.id),
                            }}
                            activeOptions={{ exact: false }}
                            className={profileLinkClassName}
                        >
                            <span className="truncate">{profile.name}</span>
                        </Link>
                    ))}
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() =>
                            requestDiscardConfirmation(() => setIsCreateProfileDialogOpen(true))
                        }
                        className={`${profileLinkClassName} w-full justify-start gap-2 rounded-none text-compendia-muted hover:bg-muted hover:text-foreground`}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        {t("dashboard.addProfile")}
                    </Button>
                </div>
            </CollapsibleContent>
            <DashboardProfileNameDialog
                mode="create"
                open={isCreateProfileDialogOpen}
                profileName=""
                isSubmitting={createProfileMutation.isPending}
                onOpenChange={setIsCreateProfileDialogOpen}
                onSubmit={handleCreateProfile}
            />
        </Collapsible>
    );
}
